'use client'

import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@nwwp/ui-shadcn'
import { ButtonCTA } from '~/components/ButtonCTA'
import { useTrackingUrl } from '~/hooks/use-tracking-url'
import { CTA, SCHEDULE_TRACKING_OPTIONS } from '~/utils/ctas'
import { HERO_CTA_ID } from '../Hero'

/**
 * Watches the Hero CTA element with IntersectionObserver.
 * Returns `true` when the element is intersecting (visible in viewport).
 * Defaults to `true` so StickyCTA starts hidden (hero is visible on initial load).
 */
function useIsElementInViewport(elementId: string): boolean {
  const [isVisible, setIsVisible] = useState(true)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    const element = document.getElementById(elementId)
    if (!element) return

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry) {
          setIsVisible(entry.isIntersecting)
        }
      },
      { threshold: 0, rootMargin: '-10px 0px 0px 0px' }
    )

    observerRef.current.observe(element)

    return () => {
      observerRef.current?.disconnect()
    }
  }, [elementId])

  return isVisible
}

export const StickyCTA = () => {
  const isHeroCTAInViewport = useIsElementInViewport(HERO_CTA_ID)
  const scheduleUrl = useTrackingUrl(CTA.SCHEDULE_FORM_PAGE, SCHEDULE_TRACKING_OPTIONS)

  const isVisible = !isHeroCTAInViewport

  return (
    <div
      data-sticky-cta=""
      data-visible={isVisible ? 'true' : 'false'}
      className={cn(
        'fixed inset-x-0 bottom-0 z-50',
        'flex flex-row items-center justify-between gap-3',
        'bg-card',
        'p-1.5 shadow-sticky',
        'transition-transform',
        'duration-300 tablet:px-4',
        isVisible ? 'translate-y-0' : 'translate-y-full'
      )}
    >
      <p className="text-title-xs !leading-relaxed !font-medium text-foreground">
        Set yourself up for a bright financial future
      </p>
      <Link href={scheduleUrl} passHref>
        <ButtonCTA
          variant="cta-dark"
          aria-label="Schedule a call with a Financial Advisor"
          segmentElement="Sticky CTA"
          className="h-10 min-w-[110px] px-4 tablet:h-14 tablet:px-[87.5px]"
        >
          Schedule a call
        </ButtonCTA>
      </Link>
    </div>
  )
}
